import React from 'react';
import { fetchPaymentPolicySettings, updatePaymentPolicySettings } from '../api/admin';
import { getAdminLocale, localizeApiError, t } from '../i18n';
import './PaymentPolicySettings.css';

const moneyText = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(getAdminLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const initialForm = {
  minimum_deposit_amount: '50',
  minimum_withdraw_amount: '50',
  maximum_withdraw_amount: '0',
  withdraw_bet_requirement_enabled: true,
  withdraw_bet_requirement_multiplier: '3',
};

export default function PaymentPolicySettings() {
  const [form, setForm] = React.useState(initialForm);
  const [maxEnabled, setMaxEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [meta, setMeta] = React.useState({ source: '', updated_by: '', updated_at: '' });

  const applyPolicy = React.useCallback((policy = {}) => {
    const max = String(policy.maximum_withdraw_amount ?? '0');
    setForm({
      minimum_deposit_amount: String(policy.minimum_deposit_amount ?? '50'),
      minimum_withdraw_amount: String(policy.minimum_withdraw_amount ?? '50'),
      maximum_withdraw_amount: max,
      withdraw_bet_requirement_enabled: policy.withdraw_bet_requirement_enabled !== false,
      withdraw_bet_requirement_multiplier: String(policy.withdraw_bet_requirement_multiplier ?? 3),
    });
    setMaxEnabled(Number(max) > 0);
    setMeta({
      source: policy.source || '',
      updated_by: policy.updated_by || '',
      updated_at: policy.updated_at || '',
    });
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchPaymentPolicySettings();
      applyPolicy(result?.policy || {});
    } catch (e) {
      setError(localizeApiError(e, '결제 정책을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [applyPolicy]);

  React.useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setMessage('');
    setError('');
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const moneyKeys = ['minimum_deposit_amount', 'minimum_withdraw_amount'];
    for (const key of moneyKeys) {
      const raw = String(form[key] ?? '').trim();
      if (!/^\d+(?:\.\d{1,2})?$/.test(raw) || Number(raw) < 0) {
        return t('최소 충전·출금금액은 0 USDT 이상이며 소수점은 2자리까지만 사용할 수 있습니다.');
      }
    }
    const maxRaw = maxEnabled ? String(form.maximum_withdraw_amount ?? '').trim() : '0';
    if (!/^\d+(?:\.\d{1,2})?$/.test(maxRaw) || Number(maxRaw) < 0) {
      return t('최대 출금금액 형식이 올바르지 않습니다.');
    }
    if (maxEnabled && Number(maxRaw) <= 0) return t('최대 출금 제한을 사용하려면 0보다 큰 금액을 입력하세요.');
    if (maxEnabled && Number(maxRaw) < Number(form.minimum_withdraw_amount || 0)) {
      return t('최대 출금금액은 최소 출금금액보다 작을 수 없습니다.');
    }
    const multiplier = Number(form.withdraw_bet_requirement_multiplier);
    if (!Number.isInteger(multiplier) || multiplier < 1 || multiplier > 100) {
      return t('출금 전 베팅 요구 배수는 1~100 사이 정수로 입력하세요.');
    }
    return '';
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        minimum_deposit_amount: form.minimum_deposit_amount,
        minimum_withdraw_amount: form.minimum_withdraw_amount,
        maximum_withdraw_amount: maxEnabled ? form.maximum_withdraw_amount : '0',
        withdraw_bet_requirement_enabled: form.withdraw_bet_requirement_enabled,
        withdraw_bet_requirement_multiplier: Number(form.withdraw_bet_requirement_multiplier),
      };
      const result = await updatePaymentPolicySettings(payload);
      applyPolicy(result?.policy || payload);
      setMessage(result?.message || t('결제 정책이 즉시 적용되었습니다.'));
    } catch (e) {
      setError(localizeApiError(e, '결제 정책 저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const updatedAtText = meta.updated_at ? new Date(meta.updated_at).toLocaleString(getAdminLocale()) : '-';

  return (
    <section className="payment-policy-page">
      <div className="payment-policy-heading">
        <div>
          <h1>{t('결제 정책 설정')}</h1>
          <p>{t('입금·출금 금액 제한과 입금액 기준 출금 전 베팅 요구 배수를 변경합니다. 저장 즉시 신규 결제 처리에 적용됩니다.')}</p>
        </div>
        <button type="button" className="policy-refresh" onClick={load} disabled={loading || saving}>{t('새로고침')}</button>
      </div>

      {error ? <div className="policy-alert is-error">{error}</div> : null}
      {message ? <div className="policy-alert is-success">{message}</div> : null}

      <div className="policy-status-grid">
        <div><span>{t('정책 소스')}</span><b>{meta.source === 'database' ? t('DB 동적 설정') : t('환경변수 기본값')}</b></div>
        <div><span>{t('마지막 변경 관리자')}</span><b>{meta.updated_by || '-'}</b></div>
        <div><span>{t('마지막 변경 시각')}</span><b>{updatedAtText}</b></div>
      </div>

      <div className="policy-panel">
        <div className="policy-section-title">
          <h2>{t('입금 제한')}</h2>
          <span>{t('최소 입금금액 0은 하한 제한 없음입니다. 실제 입금 신청은 0보다 커야 합니다.')}</span>
        </div>
        <label className="policy-field">
          <span>{t('최소 입금금액')}</span>
          <div className="policy-money-input">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.minimum_deposit_amount}
              onChange={(e) => setField('minimum_deposit_amount', e.target.value)}
              disabled={loading || saving}
            />
            <em>USDT</em>
          </div>
        </label>
      </div>

      <div className="policy-panel">
        <div className="policy-section-title">
          <h2>{t('출금 금액 제한')}</h2>
          <span>{t('최소 출금금액 0은 하한 제한 없음입니다. 최대 출금 제한은 필요할 때만 켤 수 있습니다.')}</span>
        </div>
        <div className="policy-two-columns">
          <label className="policy-field">
            <span>{t('최소 출금금액')}</span>
            <div className="policy-money-input">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.minimum_withdraw_amount}
                onChange={(e) => setField('minimum_withdraw_amount', e.target.value)}
                disabled={loading || saving}
              />
              <em>USDT</em>
            </div>
          </label>

          <div className="policy-field">
            <div className="policy-field-switch-row">
              <span>{t('최대 출금금액 제한')}</span>
              <button
                type="button"
                className={`policy-switch ${maxEnabled ? 'is-on' : ''}`}
                onClick={() => setMaxEnabled((v) => !v)}
                disabled={loading || saving}
                aria-pressed={maxEnabled}
              >
                <i />
                <b>{maxEnabled ? 'ON' : 'OFF'}</b>
              </button>
            </div>
            <div className={`policy-money-input ${!maxEnabled ? 'is-disabled' : ''}`}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.maximum_withdraw_amount}
                onChange={(e) => setField('maximum_withdraw_amount', e.target.value)}
                disabled={!maxEnabled || loading || saving}
              />
              <em>USDT</em>
            </div>
          </div>
        </div>
      </div>

      <div className="policy-panel">
        <div className="policy-section-title">
          <h2>{t('출금 전 베팅 기준')}</h2>
          <span>{t('입금 완료 시 입금액 × 배수만큼 베팅 요구액이 생성됩니다.')}</span>
        </div>
        <div className="policy-requirement-row">
          <div className="policy-field-switch-row requirement-switch">
            <div>
              <span>{t('입금액 기준 베팅 요구')}</span>
              <small>{t('OFF면 출금 시 해당 베팅 요구 원장을 검사하지 않습니다.')}</small>
            </div>
            <button
              type="button"
              className={`policy-switch ${form.withdraw_bet_requirement_enabled ? 'is-on' : ''}`}
              onClick={() => setField('withdraw_bet_requirement_enabled', !form.withdraw_bet_requirement_enabled)}
              disabled={loading || saving}
              aria-pressed={form.withdraw_bet_requirement_enabled}
            >
              <i />
              <b>{form.withdraw_bet_requirement_enabled ? 'ON' : 'OFF'}</b>
            </button>
          </div>

          <label className={`policy-field multiplier-field ${!form.withdraw_bet_requirement_enabled ? 'is-disabled' : ''}`}>
            <span>{t('요구 배수')}</span>
            <div className="policy-multiplier-input">
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={form.withdraw_bet_requirement_multiplier}
                onChange={(e) => setField('withdraw_bet_requirement_multiplier', e.target.value)}
                disabled={!form.withdraw_bet_requirement_enabled || loading || saving}
              />
              <em>{t('배')}</em>
            </div>
          </label>
        </div>
        <div className="policy-example">
          {t('예시: 최소 입금 {minimum} USDT / 베팅 배수 {multiplier}배 설정 시, 100 USDT 입금 완료 후 요구 베팅액은 {required} USDT입니다.', {
            minimum: moneyText(form.minimum_deposit_amount),
            multiplier: form.withdraw_bet_requirement_multiplier || 0,
            required: moneyText(100 * Number(form.withdraw_bet_requirement_multiplier || 0)),
          })}
        </div>
      </div>

      <div className="policy-warning">
        <b>{t('적용 기준')}</b>
        <p>{t('금액 제한은 저장 이후 생성되는 신규 요청부터 적용됩니다. 베팅 배수를 바꿔도 이미 생성된 기존 유저의 요구 베팅액은 소급 재계산하지 않고, 이후 확정되는 입금부터 새 배수가 적용됩니다.')}</p>
      </div>

      <div className="policy-actions">
        <button type="button" className="policy-save" onClick={save} disabled={loading || saving}>
          {saving ? t('저장 중...') : t('설정 저장')}
        </button>
      </div>
    </section>
  );
}
